import damRequestsTimeouts from '../../consts/dam/damRequestsTimeouts';
import damStatusTypes from '../../consts/dam/damStatusTypes';
import DeviceDisconnectedError from '../../errors/deviceDisconnectedError';
import {selectIsOnline} from '../../state/selectors/global';
import {
    selectCurrentDeviceKit,
    selectCurrentDusn,
    selectDeviceFirmware,
    selectDeviceSynchronizeUpdateDate,
    selectIotDevice,
    selectIotDeviceConnected,
    selectIotDevicesList,
    selectIsCurrentDusnAutoSelected,
} from '../../state/selectors/iotDevices';
import {autoSelectCurrentDusn, updateDeviceData} from '../../state/slice/iotDevices';
import {dispatch, getState} from '../../state/store';
import helpers from '../../utils/helpers';
import deviceHelpers from '../device/deviceHelpers';
import log from '../logger/log';
import storageService from '../storageService';
import damService from './damService';

const isUamf = (dusn) => deviceHelpers.getIsUamf(dusn);

const checkIsDeviceConnected = (dusn) => {
    const isDeviceConnected = selectIotDeviceConnected(dusn)(getState());

    if (!isDeviceConnected) {
        log.info(`damDeviceHelpers: Device is disconnected. dusn: ${dusn}`);

        throw new DeviceDisconnectedError();
    }
};

const waitDeviceConnected = (dusn) => {
    const CHECK_CONNECTED_INTERVAL = 1_000;
    const HTTP_CHECK_INTERVAL_MS = 10_000;

    return new Promise((resolve, reject) => {
        const startTime = Date.now();
        let lastHttpCheck = Date.now();

        log.debug(`damDeviceHelpers: Start waiting for device connection. dusn: ${dusn}`);

        const checkDeviceConnected = async () => {
            const isCheckStatusByHttp = Date.now() - lastHttpCheck >= HTTP_CHECK_INTERVAL_MS;

            if (isCheckStatusByHttp) {
                log.debug(`damDeviceHelpers: Check device connection status by HTTP. dusn: ${dusn}`);

                await damService.getDeviceConnectionStatus(dusn);
                lastHttpCheck = Date.now();
            }

            const {charger, holder} = selectCurrentDeviceKit(getState());

            const isHolderDusn = holder?.serialNumber === dusn;

            const isChargerConnected = selectIotDeviceConnected(charger?.serialNumber)(getState());
            const isHolderConnected = selectIotDeviceConnected(holder?.serialNumber)(getState());

            // If the charger is connected, do not wait for the holder connection.
            const isDeviceConnected = isHolderDusn ? isChargerConnected && isHolderConnected : isChargerConnected;

            if (isDeviceConnected) {
                log.debug(`damDeviceHelpers: Device connected successfully. dusn: ${dusn}`, dusn);

                return resolve();
            }

            const isTimeoutError = damRequestsTimeouts.CONNECTED <= Date.now() - startTime;

            if (isTimeoutError) {
                log.error(`damDeviceHelpers: Device connection timeout error. dusn: ${dusn}`, null, dusn);

                return reject(new DeviceDisconnectedError());
            }

            await helpers.timeout(CHECK_CONNECTED_INTERVAL);

            checkDeviceConnected();
        };

        checkDeviceConnected();
    });
};

const checkIsSynchronizeUpdateDateExpired = (dusn, timeout = damRequestsTimeouts.SYNCHRONIZE_UPDATE) => {
    const synchronizeUpdateDate = selectDeviceSynchronizeUpdateDate(dusn)(getState());
    let isExpired = true;

    if (synchronizeUpdateDate) {
        isExpired = synchronizeUpdateDate + timeout <= new Date();
    }

    log.info(`damDeviceHelpers: Synchronize update date isExpired: ${isExpired}. dusn: ${dusn}`);

    return isExpired;
};

const retryOperation = async (operation, args, retryCount = 1) => {
    const DELAY = 1000;
    const MAX_RETRIES = 3;

    try {
        return await operation(...args);
    } catch (e) {
        const nextRetryCount = retryCount + 1;

        if (nextRetryCount <= MAX_RETRIES) {
            await helpers.timeout(DELAY);

            log.info(`damDeviceHelpers: Retrying operation. Attempt: ${nextRetryCount}/${MAX_RETRIES}`);

            const isOnline = selectIsOnline(getState());

            if (!isOnline) {
                log.debug('damDeviceHelpers: Network is offline. Aborting retries.');

                throw e;
            }

            return retryOperation(operation, args, nextRetryCount);
        }

        log.error(`damDeviceHelpers: Max retries reached. Operation failed.`, e.message);

        throw e;
    }
};

const checkIsSynchronizeRequired = async (dusn, isNeedRetry, retryCount = 1) => {
    // UAMF don't have synchronize update date - so we can skip this check for them
    if (isUamf(dusn)) {
        return true;
    }

    const DELAY = 5000;
    const MAX_RETRIES = 3;
    let isExpired;

    isExpired = checkIsSynchronizeUpdateDateExpired(dusn);

    if (!isExpired) return false;

    await damService.setDeviceAssets(dusn, true);

    isExpired = checkIsSynchronizeUpdateDateExpired(dusn);

    if (!isExpired) return false;

    const nextRetryCount = retryCount + 1;

    if (nextRetryCount <= MAX_RETRIES && isNeedRetry) {
        await helpers.timeout(DELAY);

        return checkIsSynchronizeRequired(dusn, isNeedRetry, nextRetryCount);
    }

    return true;
};

// Picked on purpose (device manager, push, registration, etc.)
const getIsCurrentDusnChosenByUser = () => {
    const state = getState();
    const currentDevice = selectIotDevice(selectCurrentDusn(state))(state);

    if (!currentDevice) return false;

    return !selectIsCurrentDusnAutoSelected(state);
};

// Call from entry points only (app start, home page, unregister)
const resolveCurrentChargerDusn = () => {
    if (getIsCurrentDusnChosenByUser()) return;

    const [charger] = deviceHelpers.getSortedChargers(selectIotDevicesList(getState()));

    dispatch(autoSelectCurrentDusn(charger?.serialNumber));
};

const checkIsFirmwareUpdateInProgress = (dusn) => {
    const firmwareData = selectDeviceFirmware(dusn)(getState());
    const {status, requestId} = firmwareData || {};
    const isRunning = status === damStatusTypes.RUNNING;

    return isRunning && requestId;
};

// UAMF (i5) backend doesn't provide synchronizeUpdateDate in device assets,
// so we track it manually on successful sync completion and persist it,
// since the state gets reset on app restart while UAMF has no other source for it
const setSynchronizeUpdateDate = (dusn) => {
    if (!isUamf(dusn)) return;

    const synchronizeUpdateDate = Date.now();

    dispatch(updateDeviceData(dusn, {synchronizeUpdateDate}));
    storageService.setDeviceSyncDateEntry(dusn, synchronizeUpdateDate);
};

export default {
    isUamf,
    setSynchronizeUpdateDate,
    checkIsDeviceConnected,
    waitDeviceConnected,
    checkIsSynchronizeUpdateDateExpired,
    retryOperation,
    checkIsSynchronizeRequired,
    resolveCurrentChargerDusn,
    checkIsFirmwareUpdateInProgress,
};
